/**
 * Convert a masked US date of birth (MM/DD/YYYY, as collected by
 * MaskedDateOfBirthInput) to the ISO 8601 calendar date (YYYY-MM-DD) the CMS
 * Integration Service / FHIR Patient.birthDate expects.
 *
 * Returns '' for any value that is not a real calendar date in MM/DD/YYYY
 * form, so callers can guard against firing a request with a malformed date.
 */
export const toIsoBirthDate = (mmddyyyy: string): string => {
  // Structural check: exactly MM/DD/YYYY with digits only. This is an exported
  // utility feeding a CMS FHIR field directly, so it must not assume the caller
  // (MaskedDateOfBirthInput) already sanitized the value.
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(mmddyyyy)) return '';
  const [month, day, year] = mmddyyyy.split('/');
  const m = Number(month);
  const d = Number(day);
  const y = Number(year);
  // Reject impossible month/day ranges (e.g. "13/45/2020") before calendar check.
  if (m < 1 || m > 12 || d < 1 || d > 31) return '';
  // Plausible DOB year: not before 1900, not in the future.
  const currentYear = new Date().getFullYear();
  if (y < 1900 || y > currentYear) return '';
  // Calendar check: Date rolls invalid days (02/31 → Mar 2/3); reject those.
  const parsed = new Date(y, m - 1, d);
  if (parsed.getFullYear() !== y || parsed.getMonth() !== m - 1 || parsed.getDate() !== d) {
    return '';
  }
  return `${year}-${month}-${day}`;
};
